/* eslint-disable react-hooks/exhaustive-deps */
import {
  AddCircleOutlineOutlined,
  Check,
  Clear,
  Delete,
  Edit,
} from "@mui/icons-material";
import { Box, Grid, IconButton, Skeleton, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import ContactInfoAPI from "../../../api/contactInfo";
import EmailForm from "../../../components/contact-info/EmailForm";
import PhoneForm from "../../../components/contact-info/PhoneForm";
import { useAlert } from "../../../lib/alert";
import eventEmitter from "../../../lib/event";
import { Organization } from "../../../model";
import { Contact, ContactLabels, ContactType } from "../../../model/Contact";

interface ManageContactInfoProps {
  organization: Organization;
  userId?: string;
  type: ContactType;
  label: ContactLabels;
  hideLabelText?: boolean | undefined;
}

const ManageContactInfo: FC<ManageContactInfoProps> = ({
  type,
  label,
  userId,
  organization,
  hideLabelText,
}) => {
  const [loadedInfo, setLoadedInfo] = useState<Contact | undefined>();
  const [labelString, setLabelString] = useState<string>("");
  const [loaded, setLoaded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [userInput, setUserInput] = useState<string | undefined>(undefined);
  const [isValid, setIsValid] = useState<boolean>(false);
  const alert = useAlert();

  const handleDelete = () => {
    if (loaded && loadedInfo)
      ContactInfoAPI.deleteById(loadedInfo?.id)
        .then(() => {
          alert.success(
            "Successfully removed " + labelString.toLowerCase() + "!",
          );
          setLoadedInfo(undefined);
          setUserInput("");
        })
        .catch(() => {
          alert.error(`Could not remove ${labelString.toLowerCase()}.`);
        });
  };

  const handleSubmit = () => {
    if (userInput == loadedInfo?.value) {
      setEditing(false);
      setCreating(false);
      return;
    }

    if (userInput && userInput != "") {
      // If updating value
      if (editing && loadedInfo) {
        ContactInfoAPI.updateById(loadedInfo.id, {
          organization_id: organization.id,
          user_id: userId || "",
          contact_type: type,
          label: label,
          value: userInput || "",
          id: "",
        })
          .then(() => {
            loadInfo();
            setEditing(false);
            setCreating(false);
            alert.success(`Successfully updated ${labelString.toLowerCase()}!`);
          })
          .catch(() => {
            alert.error(`Could not update ${labelString.toLowerCase()}.`);
          });

        // if creating value
      } else if (creating) {
        ContactInfoAPI.create({
          organization_id: organization.id,
          user_id: userId || "",
          contact_type: type,
          label: label,
          value: userInput || "",
          id: "",
        })
          .then(() => {
            loadInfo();
            setEditing(false);
            setCreating(false);
            alert.success(
              "Successfully created " + labelString.toLowerCase() + "!",
            );
          })
          .catch(() => {
            alert.error(`Could not create ${labelString.toLowerCase()}.`);
          });
      }
    }
  };

  const loadInfo = () => {
    ContactInfoAPI.findOneByDetailsGlobal(
      organization.id,
      label,
      type,
      userId && userId != "" ? userId : undefined,
    ).then((res) => {
      const contact = res as Contact;
      if (contact) {
        setLoadedInfo(contact);
      } else {
        setLoadedInfo(undefined);
      }
      setLoaded(true);
      setIsValid(true);
    });
  };

  const formatDisplayInfo = (input: string) => {
    if (type === ContactType.PHONE) {
      const digits = input.replace(/\D/g, "");

      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else {
        return input;
      }
    }

    if (type === ContactType.EMAIL) {
      return input.toLowerCase();
    }

    return input;
  };

  const updateLabelString = useCallback(() => {
    let str = "Email";
    if (type == ContactType.PHONE) str = "Phone";

    if (hideLabelText) {
      setLabelString(str);
      return;
    }

    if (label == ContactLabels.BILLING) setLabelString("Billing " + str);
    else if (label == ContactLabels.PRIMARY) setLabelString("Primary " + str);
    else if (label == ContactLabels.SECONDARY)
      setLabelString("Secondary " + str);
  }, [label, type, hideLabelText]);

  const RenderControls: FC<unknown> = () => {
    if (loadedInfo && !editing && !creating)
      return (
        <>
          <Grid item xs={1}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              sx={{
                background: "#ffffff",
              }}
            >
              <IconButton
                onClick={() => {
                  setUserInput(loadedInfo.value);
                  setCreating(false);
                  setEditing(true);
                }}
              >
                <Edit />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={1}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              sx={{
                background: "#ffffff",
              }}
            >
              <IconButton onClick={handleDelete}>
                <Delete />
              </IconButton>
            </Box>
          </Grid>
        </>
      );

    if (editing || creating)
      return (
        <>
          <Grid item xs={1}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              sx={{
                background: "#ffffff",
              }}
            >
              <IconButton disabled={!isValid} onClick={handleSubmit}>
                <Check />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={1}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              sx={{
                background: "#ffffff",
              }}
            >
              <IconButton
                onClick={() => {
                  if (editing) setEditing(false);
                  if (creating) setCreating(false);
                }}
              >
                <Clear />
              </IconButton>
            </Box>
          </Grid>
        </>
      );

    return (
      <>
        <Grid item xs={1}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            sx={{
              background: "#ffffff",
            }}
          ></Box>
        </Grid>
        <Grid item xs={1}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            sx={{
              background: "#ffffff",
            }}
          >
            <IconButton
              onClick={() => {
                setEditing(false);
                setCreating(true);
              }}
            >
              <AddCircleOutlineOutlined />
            </IconButton>
          </Box>
        </Grid>
      </>
    );
  };

  useEffect(() => {
    updateLabelString();
  }, [label]);

  useEffect(() => {
    loadInfo();
  }, [organization.id]);

  useEffect(() => {
    eventEmitter.on("subscription:updated", () => {
      loadInfo();
    });
    return () => {
      eventEmitter.off("subscription:updated", () => {
        loadInfo();
      });
    };
  }, []);

  return (
    <Grid container item xs={12} height={"55px"}>
      <Grid item xs={3}>
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          sx={{
            backgroundColor: "#fafafa",
            borderRight: "1px solid #aaaaaa",
            px: "9px",
            py: "7px",
          }}
        >
          <Typography>
            <b>{labelString}</b>
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={7}>
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          sx={{ px: "9px", py: "0px", backgroundColor: "#ffffff" }}
        >
          {loaded &&
            (editing || creating) &&
            (type == ContactType.EMAIL ? (
              <EmailForm
                autoFocus
                size="small"
                variant="standard"
                defaultValue={userInput}
                onChange={(val) => {
                  setUserInput(val);
                }}
                isValid={setIsValid}
              />
            ) : type == ContactType.PHONE ? (
              <PhoneForm
                defaultValue={userInput}
                size="small"
                variant="standard"
                autoFocus
                onChange={(val) => {
                  setUserInput(val);
                }}
                isValid={setIsValid}
              />
            ) : (
              <Typography>Error Loading</Typography>
            ))}
          {loaded && !(editing || creating) && (
            <Typography>
              {loadedInfo?.value && loadedInfo.value !== "" ? (
                formatDisplayInfo(loadedInfo.value)
              ) : (
                <span style={{ color: "#888888" }}>
                  <i>{`Add ${labelString.toLowerCase()}`}</i>
                </span>
              )}
            </Typography>
          )}
          {!loaded && <Skeleton width={"100%"} height="40px" />}
        </Box>
      </Grid>

      <RenderControls />
    </Grid>
  );
};

export default ManageContactInfo;
